import React, { useMemo } from 'react';
import { Activity } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  Bar,
  Line,
  Cell,
  Legend,
} from 'recharts';
import { formatCurrency } from './formatters';

export interface TradeChartPoint {
  label: string;
  fullDate: string;
  cumulativePnL: number;
  tradePnL: number;
  tradePnLBar: number;
  userName: string;
  symbol: string;
  side: string;
  quantity: number;
  entryPrice: number;
  exitPrice: number;
  tradeCount: number;
  cumulativeByTrader: Record<string, number>;
}

export interface TraderColorConfig {
  line: string;
  profitBar: string;
  lossBar: string;
}

export interface TraderLineConfig {
  userId: string;
  userName: string;
  colors: TraderColorConfig;
}

interface PerformanceChartSectionProps {
  data: TradeChartPoint[];
  traders: TraderLineConfig[];
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: TradeChartPoint; dataKey?: string }>;
}

export const PerformanceChartSection: React.FC<PerformanceChartSectionProps> = ({ data, traders }) => {
  const formattedData = useMemo(
    () =>
      data.map((point, index) => ({
        ...point,
        index,
      })),
    [data],
  );

  const traderColorMap = useMemo(() => {
    const colorSets: TraderColorConfig[] = [
      { line: '#0f766e', profitBar: '#86efac', lossBar: '#fecaca' },
      { line: '#115e59', profitBar: '#4ade80', lossBar: '#f87171' },
      { line: '#134e4a', profitBar: '#22c55e', lossBar: '#fb7185' },
      { line: '#166534', profitBar: '#16a34a', lossBar: '#f43f5e' },
      { line: '#1a4940', profitBar: '#15803d', lossBar: '#e11d48' },
      { line: '#1f3c3a', profitBar: '#0f766e', lossBar: '#be185d' },
      { line: '#22534f', profitBar: '#0d9488', lossBar: '#c026d3' },
    ];

    const map: Record<string, TraderColorConfig> = {};
    traders.forEach(({ userName }, index) => {
      map[userName] = colorSets[index % colorSets.length];
    });

    return map;
  }, [traders]);

  const hasMultipleTraders = traders.length > 1;

  const renderLegend = () => (
    <div className="flex flex-wrap gap-4 text-xs text-gray-700 px-2 pb-2">
      {traders.map(({ userName }) => {
        const colors = traderColorMap[userName];
        return (
          <div key={userName} className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <span className="h-1.5 w-6 rounded-full" style={{ backgroundColor: colors.line }} />
              {userName}
            </span>
            <span className="flex items-center gap-1 text-gray-500">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.profitBar }} />
              Win
            </span>
            <span className="flex items-center gap-1 text-gray-500">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.lossBar }} />
              Loss
            </span>
          </div>
        );
      })}
    </div>
  );
 

  const renderTooltip = ({ active, payload }: ChartTooltipProps) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const barEntry = payload.find((entry) => entry && entry.dataKey === 'tradePnLBar');
    const pointPayload = barEntry?.payload as TradeChartPoint | undefined;
    if (!barEntry || !pointPayload) {
      return null;
    }

    const traderColors = traderColorMap[pointPayload.userName];

    return (
      <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg max-w-xs">
        <p className="font-medium text-gray-900">{pointPayload.fullDate}</p>
        <p className="text-sm text-gray-600">Trade #{pointPayload.tradeCount}</p>
        <div className="mt-2 space-y-1 text-sm">
          <div className="inline-flex items-center gap-2">
            <span className="text-gray-600">Trader:</span>
            {traderColors && (
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: traderColors.line }} />
            )}
            <span>{pointPayload.userName}</span>
          </div>
          <div>
            <span className="text-gray-600">Symbol:</span> {pointPayload.symbol}
          </div>
          <div>
            <span className="text-gray-600">Side:</span> {pointPayload.side}
          </div>
          <div>
            <span className="text-gray-600">Quantity:</span> {pointPayload.quantity.toFixed(4)}
          </div>
          <div>
            <span className="text-gray-600">Entry Price:</span> {formatCurrency(pointPayload.entryPrice)}
          </div>
          <div>
            <span className="text-gray-600">Exit Price:</span> {formatCurrency(pointPayload.exitPrice)}
          </div>
          <div>
            <span className="text-gray-600">Trade P&L:</span>{' '}
            <span className={pointPayload.tradePnL >= 0 ? 'text-green-600' : 'text-red-600'}>
              {formatCurrency(pointPayload.tradePnL)}
            </span>
          </div>
          <div className="font-medium">
            <span className="text-gray-600">Cumulative P&L:</span>{' '}
            <span className={pointPayload.cumulativePnL >= 0 ? 'text-green-600' : 'text-red-600'}>
              {formatCurrency(pointPayload.cumulativePnL)}
            </span>
          </div>
        </div>
        {hasMultipleTraders && pointPayload.cumulativeByTrader && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs uppercase tracking-wide text-gray-500">Cumulative by trader</p>
            <div className="mt-2 space-y-1">
              {traders.map(({ userName, colors }) => {
                const value = pointPayload.cumulativeByTrader?.[userName];
                if (value === undefined) {
                  return null;
                }
                return (
                  <div key={userName} className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-2 text-gray-700">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colors.line }} />
                      {userName}
                    </span>
                    <span className={value >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (formattedData.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Trading Performance Analysis</h2>
          <p className="text-sm text-gray-600 mt-1">
            Cumulative P&L (blue line) and individual trade results (green/red bars)
          </p>
        </div>
        <div className="h-96 flex items-center justify-center">
          <div className="text-center">
            <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Trading Data</h3>
            <p className="text-gray-600">No closed trades available for the selected period.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Trading Performance Analysis</h2>
        <p className="text-sm text-gray-600 mt-1">
          Trader-specific cumulative P&L lines with color-coded trade results
        </p>
      </div>
      <div className="p-6">
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={formattedData} margin={{ top: 40, right: 30, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                yAxisId="left"
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value.toFixed(0)}`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#6b7280"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value.toFixed(0)}`}
              />
              <Tooltip content={renderTooltip} cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }} />
              <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" />
              <Bar yAxisId="right" dataKey="tradePnLBar" opacity={0.95} legendType="none">
                {formattedData.map((entry) => {
                  const colors = traderColorMap[entry.userName];
                  const isWin = entry.tradePnLBar >= 0;
                  const fill = isWin
                    ? colors?.profitBar ?? '#4ade80'
                    : colors?.lossBar ?? '#f87171';
                  return <Cell key={`cell-${entry.index}`} fill={fill} opacity={0.95} />;
                })}
              </Bar>
              {traders.map(({ userName, colors }) => (
                <Line
                  key={`line-${userName}`}
                  yAxisId="left"
                  type="monotone"
                  dataKey={(datum: TradeChartPoint) => datum.cumulativeByTrader[userName] ?? null}
                  stroke={colors.line}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{
                    r: 4,
                    stroke: colors.line,
                    strokeWidth: 2,
                    fill: '#ffffff',
                  }}
                  connectNulls
                  name={userName}
                />
              ))}
              <Legend
                verticalAlign="top"
                align="left"
                wrapperStyle={{ paddingBottom: 8 }}
                formatter={() => null}
                content={renderLegend}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
